import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { MessageCircle, ChevronRight, MapPin, Plane, Bus, Hotel, Mountain, Ticket, User } from 'lucide-react';

const TripConfigurator = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    destination: '',
    flights: '',
    transfers: '',
    hotel: '',
    skiServices: [],
    passes: '',
    adults: '',
    children: '',
    weight: '',
    height: '',
    shoeSize: '',
    skiClasses: '',
    name: '',
    email: '',
    phone: '',
    comments: ''
  });

  const destinations = [
    'CERRO CASTOR - USHUAIA',
    'CERRO CATEDRAL - BARILOCHE',
    'LAS LEÑAS - MENDOZA',
    'CERRO CHAPELCO - SAN MARTÍN DE LOS ANDES',
    'CERRO BAYO - VILLA LA ANGOSTURA',
    'LA HOYA - ESQUEL',
    'CAVIAHUE - NEUQUÉN',
    'VALLE NEVADO - CHILE',
    'PERITO MORENO - SANTA CRUZ'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (service) => {
    setFormData(prev => ({
      ...prev,
      skiServices: prev.skiServices.includes(service)
        ? prev.skiServices.filter(s => s !== service)
        : [...prev.skiServices, service]
    }));
  };

  const generateWhatsAppMessage = () => {
    const message = `🏔️ *Consulta de Viaje de Esquí - Ando Viajando*\n\n` +
      `*Nombre:* ${formData.name}\n` +
      `*Email:* ${formData.email}\n` +
      `*Teléfono:* ${formData.phone}\n\n` +
      `*DESTINO:* ${formData.destination}\n` +
      `*COMPOSICIÓN FAMILIAR:* ${formData.adults || '0'} adultos, ${formData.children || '0'} niños\n` +
      `*VUELOS:* ${formData.flights}\n` +
      `*TRASLADOS:* ${formData.transfers}\n` +
      `*HOTEL:* ${formData.hotel}\n` +
      `*SERVICIOS ESQUÍ:* ${formData.skiServices.join(', ') || 'Ninguno'}\n` +
      `*PASES:* ${formData.passes}\n` +
      `*EQUIPO - Peso:* ${formData.weight}kg | *Altura:* ${formData.height}cm | *Calzado:* ${formData.shoeSize}\n` +
      `*CLASES:* ${formData.skiClasses}\n\n` +
      `*Comentarios:* ${formData.comments || 'Ninguno'}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/5491135981641?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const nextStep = () => {
    if (currentStep < 9) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const stepIcons = [MapPin, Plane, User, Bus, Hotel, Mountain, Ticket, User, MessageCircle];
  const CurrentStepIcon = stepIcons[currentStep - 1];

  return (
    <section id="configurator" className="py-20 bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#003580] mb-4">
            Configurá Tu Viaje de Esquí
          </h2>
          <p className="text-xl text-gray-600">Plan Your Ski Trip</p>
          <p className="text-gray-500 mt-2">Completar los pasos y enviar consulta por WhatsApp</p>
        </div>

        {/* Progress Bar */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((step) => (
              <React.Fragment key={step}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                    step <= currentStep
                      ? 'bg-[#FF6B35] text-white scale-110 shadow-lg'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step}
                </div>
                {step < 9 && (
                  <div
                    className={`flex-1 h-1 mx-2 transition-all duration-300 ${
                      step < currentStep ? 'bg-[#FF6B35]' : 'bg-gray-200'
                    }`}
                  ></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <Card className="max-w-4xl mx-auto shadow-2xl border-2 border-gray-100">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-white border-b-2 border-gray-100">
            <CardTitle className="flex items-center space-x-3 text-2xl text-[#003580]">
              <CurrentStepIcon className="w-8 h-8 text-[#FF6B35]" />
              <span>
                {currentStep === 1 && 'Paso 1: Destino / Step 1: Destination'}
                {currentStep === 2 && 'Paso 2: Composición Familiar / Step 2: Family Composition'}
                {currentStep === 3 && 'Paso 3: Vuelos / Step 3: Flights'}
                {currentStep === 4 && 'Paso 4: Traslados / Step 4: Transfers'}
                {currentStep === 5 && 'Paso 5: Hotel / Step 5: Hotel'}
                {currentStep === 6 && 'Paso 6: Servicios de Esquí / Step 6: Ski Services'}
                {currentStep === 7 && 'Paso 7: Pases / Step 7: Passes'}
                {currentStep === 8 && 'Paso 8: Equipos y Clases / Step 8: Equipment & Classes'}
                {currentStep === 9 && 'Paso 9: Datos de Contacto / Step 9: Contact Info'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            {/* Step 1: Destination */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold text-[#003580]">
                  Seleccioná tu destino / Select your destination
                </Label>
                <RadioGroup value={formData.destination} onValueChange={(val) => handleChange('destination', val)}>
                  {destinations.map((dest) => (
                    <div key={dest} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                      <RadioGroupItem value={dest} id={dest} />
                      <Label htmlFor={dest} className="cursor-pointer flex-1 text-gray-700">{dest}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Step 2: Composición Familiar */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold text-[#003580] mb-4 block">
                  Composición familiar / Family composition
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="adults" className="text-gray-700 font-semibold">
                      Cantidad de adultos / Number of adults
                    </Label>
                    <Input
                      id="adults"
                      type="number"
                      min="0"
                      placeholder="Ej: 2"
                      value={formData.adults}
                      onChange={(e) => handleChange('adults', e.target.value)}
                      className="border-gray-300 focus:border-[#FF6B35] focus:ring-[#FF6B35] text-lg p-4"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="children" className="text-gray-700 font-semibold">
                      Cantidad de niños / Number of children
                    </Label>
                    <Input
                      id="children"
                      type="number"
                      min="0"
                      placeholder="Ej: 1"
                      value={formData.children}
                      onChange={(e) => handleChange('children', e.target.value)}
                      className="border-gray-300 focus:border-[#FF6B35] focus:ring-[#FF6B35] text-lg p-4"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500 italic mt-4">
                  Esta información nos ayuda a personalizar tu experiencia / This helps us personalize your experience
                </p>
              </div>
            )}

            {/* Step 3: Flights */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold text-[#003580]">
                  ¿Necesitás vuelos? / Do you need flights?
                </Label>
                <RadioGroup value={formData.flights} onValueChange={(val) => handleChange('flights', val)}>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Sí, ida y vuelta" id="flights-yes" />
                    <Label htmlFor="flights-yes" className="cursor-pointer flex-1 text-gray-700">Sí, ida y vuelta / Yes, round trip</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Sólo ida" id="flights-oneway" />
                    <Label htmlFor="flights-oneway" className="cursor-pointer flex-1 text-gray-700">Sólo ida / One way only</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="No, tengo mi propio transporte" id="flights-no" />
                    <Label htmlFor="flights-no" className="cursor-pointer flex-1 text-gray-700">No, tengo mi propio transporte / No, I have my own transport</Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Step 4: Transfers - CORRECCIÓN DEL ERROR */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold text-[#003580]">
                  ¿Necesitás traslados? / Do you need transfers?
                </Label>
                <RadioGroup value={formData.transfers} onValueChange={(val) => handleChange('transfers', val)}>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Sí, aeropuerto-hotel-aeropuerto y traslados al cerro" id="transfers-full" />
                    <Label htmlFor="transfers-full" className="cursor-pointer flex-1 text-gray-700">Sí, aeropuerto-hotel-aeropuerto y traslados al cerro / Yes, airport-hotel-airport and transfers to ski resort</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Sólo traslados al centro de esquí" id="transfers-ski" />
                    <Label htmlFor="transfers-ski" className="cursor-pointer flex-1 text-gray-700">Sólo traslados al centro de esquí / Only transfers to ski resort</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Sólo aeropuerto-hotel-aeropuerto" id="transfers-airport-hotel" />
                    <Label htmlFor="transfers-airport-hotel" className="cursor-pointer flex-1 text-gray-700">Sólo aeropuerto-hotel-aeropuerto / Only airport-hotel-airport</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="No necesito" id="transfers-no" />
                    <Label htmlFor="transfers-no" className="cursor-pointer flex-1 text-gray-700">No necesito / I don't need</Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Step 5: Hotel */}
            {currentStep === 5 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold text-[#003580]">
                  Tipo de alojamiento / Type of accommodation
                </Label>
                <RadioGroup value={formData.hotel} onValueChange={(val) => handleChange('hotel', val)}>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Hotel 5 estrellas" id="hotel-5star" />
                    <Label htmlFor="hotel-5star" className="cursor-pointer flex-1 text-gray-700">Hotel 5 estrellas / 5-star hotel</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Hotel 4 estrellas" id="hotel-4star" />
                    <Label htmlFor="hotel-4star" className="cursor-pointer flex-1 text-gray-700">Hotel 4 estrellas / 4-star hotel</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Hotel 3 estrellas" id="hotel-3star" />
                    <Label htmlFor="hotel-3star" className="cursor-pointer flex-1 text-gray-700">Hotel 3 estrellas / 3-star hotel</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Apart hotel o cabaña" id="hotel-apart" />
                    <Label htmlFor="hotel-apart" className="cursor-pointer flex-1 text-gray-700">Apart hotel o cabaña / Apart hotel or cabin</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="No necesito, tengo alojamiento" id="hotel-no" />
                    <Label htmlFor="hotel-no" className="cursor-pointer flex-1 text-gray-700">No necesito, tengo alojamiento / I don't need, I have accommodation</Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Step 6: Ski Services */}
            {currentStep === 6 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold text-[#003580] mb-4 block">
                  Servicios de esquí (seleccioná los que necesites) / Ski services (select what you need)
                </Label>
                <div className="space-y-3">
                  {[
                    { value: 'Alquiler de esquí', label: 'Alquiler de esquí / Ski rental' },
                    { value: 'Alquiler de snowboard', label: 'Alquiler de snowboard / Snowboard rental' },
                    { value: 'Alquiler de ropa térmica', label: 'Alquiler de ropa térmica / Thermal clothing rental' },
                    { value: 'Seguro de esquí', label: 'Seguro de esquí / Ski insurance' }
                  ].map((service) => (
                    <div key={service.value} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                      <Checkbox
                        id={service.value}
                        checked={formData.skiServices.includes(service.value)}
                        onCheckedChange={() => handleCheckboxChange(service.value)}
                      />
                      <Label htmlFor={service.value} className="cursor-pointer flex-1 text-gray-700">{service.label}</Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 7: Passes */}
            {currentStep === 7 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold text-[#003580]">
                  Pases de esquí / Ski passes
                </Label>
                <RadioGroup value={formData.passes} onValueChange={(val) => handleChange('passes', val)}>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Pase semanal (7 días)" id="pass-week" />
                    <Label htmlFor="pass-week" className="cursor-pointer flex-1 text-gray-700">Pase semanal (7 días) / Weekly pass (7 days)</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Pase de 5 días" id="pass-5days" />
                    <Label htmlFor="pass-5days" className="cursor-pointer flex-1 text-gray-700">Pase de 5 días / 5-day pass</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Pase de 3 días" id="pass-3days" />
                    <Label htmlFor="pass-3days" className="cursor-pointer flex-1 text-gray-700">Pase de 3 días / 3-day pass</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                    <RadioGroupItem value="Pase diario" id="pass-daily" />
                    <Label htmlFor="pass-daily" className="cursor-pointer flex-1 text-gray-700">Pase diario / Daily pass</Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Step 8: Equipment & Classes */}
            {currentStep === 8 && (
              <div className="space-y-6">
                <div>
                  <Label className="text-lg font-semibold text-[#003580] mb-4 block">
                    Medidas para el equipo / Equipment measurements
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="weight" className="text-gray-700">Peso (kg) / Weight</Label>
                      <Input
                        id="weight"
                        type="number"
                        placeholder="Ej: 70"
                        value={formData.weight}
                        onChange={(e) => handleChange('weight', e.target.value)}
                        className="border-gray-300 focus:border-[#FF6B35] focus:ring-[#FF6B35]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="height" className="text-gray-700">Altura (cm) / Height</Label>
                      <Input
                        id="height"
                        type="number"
                        placeholder="Ej: 175"
                        value={formData.height}
                        onChange={(e) => handleChange('height', e.target.value)}
                        className="border-gray-300 focus:border-[#FF6B35] focus:ring-[#FF6B35]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="shoeSize" className="text-gray-700">Calzado / Shoe size</Label>
                      <Input
                        id="shoeSize"
                        type="number"
                        placeholder="Ej: 42"
                        value={formData.shoeSize}
                        onChange={(e) => handleChange('shoeSize', e.target.value)}
                        className="border-gray-300 focus:border-[#FF6B35] focus:ring-[#FF6B35]"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <Label className="text-lg font-semibold text-[#003580]">
                    Clases de esquí / Ski classes
                  </Label>
                  <RadioGroup value={formData.skiClasses} onValueChange={(val) => handleChange('skiClasses', val)}>
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                      <RadioGroupItem value="Clases grupales" id="class-group" />
                      <Label htmlFor="class-group" className="cursor-pointer flex-1 text-gray-700">Clases grupales / Group classes</Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                      <RadioGroupItem value="Clases privadas" id="class-private" />
                      <Label htmlFor="class-private" className="cursor-pointer flex-1 text-gray-700">Clases privadas / Private classes</Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors">
                      <RadioGroupItem value="No necesito" id="class-no" />
                      <Label htmlFor="class-no" className="cursor-pointer flex-1 text-gray-700">No necesito / I don't need</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
            )}

            {/* Step 9: Contact Info */}
            {currentStep === 9 && (
              <div className="space-y-6">
                <Label className="text-lg font-semibold text-[#003580] mb-4 block">
                  Datos de contacto / Contact information
                </Label>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-700">Nombre completo / Full name *</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Juan Pérez"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="border-gray-300 focus:border-[#FF6B35] focus:ring-[#FF6B35]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="juan@email.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="border-gray-300 focus:border-[#FF6B35] focus:ring-[#FF6B35]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-700">Teléfono / Phone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+54 9 11 1234 5678"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="border-gray-300 focus:border-[#FF6B35] focus:ring-[#FF6B35]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="comments" className="text-gray-700">Comentarios adicionales / Additional comments</Label>
                    <Textarea
                      id="comments"
                      placeholder="Comentarios, preguntas o solicitudes especiales..."
                      value={formData.comments}
                      onChange={(e) => handleChange('comments', e.target.value)}
                      className="border-gray-300 focus:border-[#FF6B35] focus:ring-[#FF6B35] min-h-[100px]"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <Button
                onClick={prevStep}
                disabled={currentStep === 1}
                variant="outline"
                className="border-[#003580] text-[#003580] hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed px-6"
              >
                Anterior / Previous
              </Button>
              {currentStep < 9 ? (
                <Button
                  onClick={nextStep}
                  className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-6 flex items-center space-x-2"
                >
                  <span>Siguiente / Next</span>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              ) : (
                <Button
                  onClick={generateWhatsAppMessage}
                  disabled={!formData.name || !formData.email || !formData.phone}
                  className="bg-[#25D366] hover:bg-[#1DA851] text-white px-8 py-6 text-lg font-bold flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <MessageCircle className="w-6 h-6" />
                  <span>CONSULTAR POR WHATSAPP</span>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default TripConfigurator;
